/* eslint react-hooks/exhaustive-deps: "warn" */
import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingScreening.module.scss';

import { type FilterGroup, type FinancialSub, type ScreeningRow } from './TransferPricingScreening.types';

const GROUPS: FilterGroup[] = [
  {
    name: 'Companies',
    count: '12',
    fields: [
      { label: 'Legal status', meta: 'active', active: true },
      { label: 'Legal form', meta: 'list' },
      { label: 'Company age', meta: 'range' },
      { label: 'Incorporation date', meta: 'range' },
      { label: 'Registration number', meta: 'exact' },
    ],
  },
  {
    name: 'Executives',
    count: '9',
    fields: [
      { label: 'Role', meta: 'list' },
      { label: 'Appointment date', meta: 'range' },
      { label: 'Directorship count', meta: 'range' },
      { label: 'Shared directors', meta: 'flag' },
    ],
  },
  {
    name: 'Industry',
    count: '6',
    fields: [
      { label: 'Activity keywords', meta: '4 set', active: true },
      { label: 'Exclusion terms', meta: '1 set', active: true },
      { label: 'Business description', meta: 'text' },
      { label: 'Products and services', meta: 'text' },
      { label: 'NACE / SIC code', meta: 'list' },
      { label: 'Primary vs secondary', meta: 'flag' },
    ],
  },
  {
    name: 'Location',
    count: '5',
    fields: [
      { label: 'Country', meta: '5 set', active: true },
      { label: 'Region', meta: 'list' },
      { label: 'City', meta: 'text' },
      { label: 'Registered address', meta: 'text' },
    ],
  },
  {
    name: 'Growth Signals',
    count: '7',
    fields: [
      { label: 'Turnover trend', meta: '3yr' },
      { label: 'Headcount trend', meta: '3yr' },
      { label: 'Status change', meta: 'event' },
      { label: 'New filing', meta: 'event' },
    ],
  },
  {
    name: 'Ownership',
    count: '8',
    fields: [
      { label: 'Largest holder %', meta: '< 25', active: true },
      { label: 'Group member', meta: 'exclude', active: true },
      { label: 'Shareholder count', meta: 'range' },
      { label: 'Ultimate parent', meta: 'entity' },
      { label: 'Subsidiary count', meta: 'range' },
      { label: 'Cross-border parent', meta: 'flag' },
    ],
  },
  {
    name: 'Digital Insights',
    count: '6',
    fields: [
      { label: 'Website present', meta: 'bool' },
      { label: 'Technologies', meta: 'list' },
      { label: 'Web traffic band', meta: 'range' },
    ],
  },
  {
    name: 'Advanced Search',
    count: '—',
    fields: [
      { label: 'Boolean builder', meta: 'query' },
      { label: 'Saved strategies', meta: 'list' },
      { label: 'Load TP-2026-0418', meta: 'loaded', active: true },
    ],
  },
];

const FIN_SUBS: FinancialSub[] = [
  { label: 'Revenue', segments: ['USD', 'EUR', 'GBP'], defaultSegment: 'EUR', range: ['8,000,000', '120,000,000'] },
  { label: 'Net Profit', segments: ['USD', 'EUR', 'GBP'], defaultSegment: 'EUR', range: ['—', '—'] },
  { label: 'Total Liabilities', segments: ['USD', 'EUR', 'GBP'], defaultSegment: 'EUR', range: ['—', '—'] },
  { label: 'Directors Remuneration', segments: ['USD', 'EUR', 'GBP'], defaultSegment: 'EUR', range: ['—', '—'] },
  { label: 'Profit per employee', segments: ['USD', 'EUR', 'GBP'], defaultSegment: 'EUR', range: ['—', '—'] },
  { label: 'Exports', segments: ['Any', 'Exporter', 'Domestic only'], defaultSegment: 'Any' },
];

const FLAGS: Record<string, string> = {
  DE: '🇩🇪',
  FR: '🇫🇷',
  NL: '🇳🇱',
  PL: '🇵🇱',
  BE: '🇧🇪',
  AT: '🇦🇹',
};

const ROWS: ScreeningRow[] = [
  {
    logo: 'NH',
    name: 'Nordwaren Handels GmbH',
    country: 'DE',
    flag: FLAGS.DE,
    status: 'Active',
    regNo: 'HRB 214880 B',
    activity: 'Wholesale, building materials',
    revenue: '41,208,000',
    margin: '3.97%',
    employees: '104',
    age: '34',
    holder: '18.4%',
    filed: '28 Apr 2026',
  },
  {
    logo: 'DL',
    name: 'Distribution Lavelle SAS',
    country: 'FR',
    flag: FLAGS.FR,
    status: 'Active',
    regNo: '812 445 907',
    activity: 'Trade distribution, plumbing',
    revenue: '27,840,000',
    margin: '2.41%',
    employees: '61',
    age: '22',
    holder: '21.0%',
    filed: '02 Jun 2026',
  },
  {
    logo: 'VG',
    name: 'Veldkamp Groothandel BV',
    country: 'NL',
    flag: FLAGS.NL,
    status: 'Active',
    regNo: '64229118',
    activity: 'Wholesale, technical supplies',
    revenue: '63,050,000',
    margin: '5.12%',
    employees: '148',
    age: '41',
    holder: '14.7%',
    filed: '19 May 2026',
  },
  {
    logo: 'DD',
    name: 'Dobrzyn Dystrybucja Sp. z o.o.',
    country: 'PL',
    flag: FLAGS.PL,
    status: 'Active',
    regNo: 'KRS 0000418823',
    activity: 'Stockist, construction',
    revenue: '11,960,000',
    margin: '4.38%',
    employees: '38',
    age: '14',
    holder: '24.1%',
    filed: '30 Jun 2026',
  },
  {
    logo: 'MV',
    name: 'Maes Verdeling NV',
    country: 'BE',
    flag: FLAGS.BE,
    status: 'Active',
    regNo: '0844.219.556',
    activity: 'Wholesale, insulation',
    revenue: '34,770,000',
    margin: '3.05%',
    employees: '89',
    age: '27',
    holder: '19.8%',
    filed: '07 Jul 2026',
  },
  {
    logo: 'BB',
    name: 'Brandhorst Baustoffe GmbH',
    country: 'DE',
    flag: FLAGS.DE,
    status: 'Active',
    regNo: 'HRB 92117',
    activity: 'Wholesale, roofing supplies',
    revenue: '18,340,000',
    margin: '2.87%',
    employees: '44',
    age: '31',
    holder: '22.6%',
    filed: '12 May 2026',
  },
  {
    logo: 'CM',
    name: 'Comptoir Marchal SAS',
    country: 'FR',
    flag: FLAGS.FR,
    status: 'Active',
    regNo: '793 118 402',
    activity: 'Trade distribution, timber',
    revenue: '15,720,000',
    margin: '4.02%',
    employees: '39',
    age: '18',
    holder: '17.3%',
    filed: '21 Apr 2026',
  },
  {
    logo: 'VD',
    name: 'Van Doorn Bouwmaterialen BV',
    country: 'NL',
    flag: FLAGS.NL,
    status: 'Active',
    regNo: '58117420',
    activity: 'Wholesale, building materials',
    revenue: '47,910,000',
    margin: '3.44%',
    employees: '112',
    age: '36',
    holder: '11.2%',
    filed: '03 Jun 2026',
  },
  {
    logo: 'KM',
    name: 'Krosno Materialy Sp. z o.o.',
    country: 'PL',
    flag: FLAGS.PL,
    status: 'Active',
    regNo: 'KRS 0000512884',
    activity: 'Stockist, insulation',
    revenue: '9,480,000',
    margin: '5.61%',
    employees: '31',
    age: '12',
    holder: '23.8%',
    filed: '27 May 2026',
  },
  {
    logo: 'SV',
    name: 'Steinlach Vertrieb AG',
    country: 'AT',
    flag: FLAGS.AT,
    status: 'Active',
    regNo: 'FN 419203 k',
    activity: 'Wholesale, drywall',
    revenue: '19,420,000',
    margin: '1.88%',
    employees: '52',
    age: '24',
    holder: '20.4%',
    filed: '14 Apr 2026',
  },
  {
    logo: 'DV',
    name: 'Delvaux Distributie NV',
    country: 'BE',
    flag: FLAGS.BE,
    status: 'Active',
    regNo: '0728.441.190',
    activity: 'Wholesale, fixings',
    revenue: '26,050,000',
    margin: '3.79%',
    employees: '68',
    age: '29',
    holder: '16.5%',
    filed: '09 Jul 2026',
  },
  {
    logo: 'HH',
    name: 'Hagemann Handel GmbH',
    country: 'DE',
    flag: FLAGS.DE,
    status: 'Active',
    regNo: 'HRB 176052',
    activity: 'Wholesale, building materials',
    revenue: '88,200,000',
    margin: '6.44%',
    employees: '197',
    age: '43',
    holder: '24.9%',
    filed: '18 Mar 2026',
  },
  {
    logo: 'LT',
    name: 'Lelieveld Techniek BV',
    country: 'NL',
    flag: FLAGS.NL,
    status: 'Active',
    regNo: '61204488',
    activity: 'Wholesale, cement products',
    revenue: '33,660,000',
    margin: '2.10%',
    employees: '74',
    age: '26',
    holder: '19.1%',
    filed: '05 Jun 2026',
  },
  {
    logo: 'WB',
    name: 'Wiener Baustoff Vertrieb GmbH',
    country: 'AT',
    flag: FLAGS.AT,
    status: 'Active',
    regNo: 'FN 388214 s',
    activity: 'Wholesale, aggregates',
    revenue: '12,880,000',
    margin: '4.75%',
    employees: '35',
    age: '16',
    holder: '13.9%',
    filed: '22 Jun 2026',
  },
  {
    logo: 'SH',
    name: 'Sudwerk Handels GmbH',
    country: 'DE',
    flag: FLAGS.DE,
    status: 'Group member',
    warn: true,
    regNo: 'HRB 118204',
    activity: 'Wholesale, building materials',
    revenue: '22,400,000',
    margin: '6.91%',
    employees: '57',
    age: '19',
    holder: '94.6%',
    filed: '11 Mar 2026',
    dim: true,
  },
];

const COLUMNS = [
  'Company name',
  'Country',
  'Status',
  'Reg. no',
  'Described activity',
  'Revenue FY2025',
  'Op. margin',
  'Employees',
  'Age',
  'Largest holder',
  'Source filing',
];

export const TransferPricingScreening = () => {
  const { t } = useInt();
  const [openGroup, setOpenGroup] = useState<string | null>('Financials');
  const [openFinSub, setOpenFinSub] = useState<string>('Revenue');
  const [finSegments, setFinSegments] = useState<Record<string, string>>(() =>
    Object.fromEntries(FIN_SUBS.map((sub) => [sub.label, sub.defaultSegment])),
  );

  return (
    <Section className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('Build the population before you argue about the method.')}
          description={t(
            'A comparability analysis is only as good as the search that produced it. This is the platform that runs it, filtering on described activity, financials, ownership and filing history in a single pass.',
          )}
        />
      </div>

      <div className={styles.appWrap}>
        <div className={styles.app}>
          <div className={styles.appTop}>
            <div className={styles.appBrand}>
              <span className={styles.appBrandMark} />
              Global Database
            </div>

            <div className={styles.appSearch}>
              <span className={styles.appSearchIcon} />
              Search for a Company or Executive
              <kbd>/</kbd>
            </div>

            <div className={styles.appWho}>
              <span>Invite User</span>
              <span className={styles.appAvatar}>NB</span>
            </div>
          </div>

          <div className={styles.appMeta}>
            <div className={styles.appCount}>
              Companies: <b>177,798,671</b> &nbsp;&amp;&nbsp; Executives: <b>276,511,547</b>
            </div>

            <span className={styles.appChip}>
              Status: <b>Active</b> <i>×</i>
            </span>
            <span className={styles.appChip}>
              Country: <b>DE, FR, NL, BE, AT, PL</b> <i>×</i>
            </span>
            <span className={styles.appChip}>
              Revenue: <b>€8m – €120m</b> <i>×</i>
            </span>
          </div>

          <div className={styles.appBody}>
            <div className={styles.rail}>
              <div className={styles.railHead}>Filters</div>

              {GROUPS.slice(0, 4).map((group) => (
                <FilterGroupRow
                  key={group.name}
                  group={group}
                  open={openGroup === group.name}
                  onToggle={setOpenGroup}
                />
              ))}

              <div className={cn(styles.group, openGroup === 'Financials' && styles.groupOpen)}>
                <button
                  type="button"
                  className={styles.groupHead}
                  onClick={() => setOpenGroup(openGroup === 'Financials' ? null : 'Financials')}
                  aria-expanded={openGroup === 'Financials'}
                >
                  <span>Financials</span>
                  <span className={styles.groupCount}>7</span>
                  <span className={styles.groupChevron} />
                </button>

                <div className={styles.groupBody}>
                  <div className={styles.groupBodyInner}>
                    {FIN_SUBS.map((sub) => (
                      <div key={sub.label} className={cn(styles.finSub, openFinSub === sub.label && styles.finSubOpen)}>
                        <button
                          type="button"
                          className={styles.finSubHead}
                          onClick={() => setOpenFinSub(openFinSub === sub.label ? '' : sub.label)}
                          aria-expanded={openFinSub === sub.label}
                        >
                          <span>{sub.label}</span>
                          <span className={styles.groupChevron} />
                        </button>

                        <div className={styles.finSubBody}>
                          <div className={styles.finSubBodyInner}>
                            <div className={styles.seg}>
                              {sub.segments.map((segment) => (
                                <button
                                  key={segment}
                                  type="button"
                                  className={cn(styles.segBtn, finSegments[sub.label] === segment && styles.segBtnOn)}
                                  onClick={() => setFinSegments((prev) => ({ ...prev, [sub.label]: segment }))}
                                >
                                  {segment}
                                </button>
                              ))}
                            </div>

                            {sub.range && (
                              <div className={styles.rng}>
                                <span className={styles.rngVal}>{sub.range[0]}</span>
                                <span className={styles.rngVal}>{sub.range[1]}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className={styles.bank}>
                      <span>Search by bank</span>
                      <span className={styles.bankInput}>
                        Bank <i>→</i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {GROUPS.slice(4).map((group) => (
                <FilterGroupRow
                  key={group.name}
                  group={group}
                  open={openGroup === group.name}
                  onToggle={setOpenGroup}
                />
              ))}
            </div>

            <div className={styles.main}>
              <div className={styles.toolbar}>
                <span>Edit view</span>
                <span>Save to list</span>
                <span>Export ▾</span>
                <span className={styles.toolbarRight}>View as report</span>
              </div>

              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.checkboxCol} />
                      {COLUMNS.map((column) => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {ROWS.map((row) => (
                      <tr key={row.name} className={cn(row.dim && styles.rowDim)}>
                        <td>
                          <span className={cn(styles.checkbox, !row.dim && styles.checkboxOn)} />
                        </td>
                        <td>
                          <span className={styles.company}>
                            <span className={styles.companyLogo}>{row.logo}</span>
                            {row.name}
                          </span>
                        </td>
                        <td>
                          <span className={styles.flagCell}>
                            {row.flag} {row.country}
                          </span>
                        </td>
                        <td>
                          <span className={cn(styles.status, row.warn && styles.statusWarn)}>
                            <span className={styles.statusDot} />
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <span className={styles.mono}>{row.regNo}</span>
                        </td>
                        <td>{row.activity}</td>
                        <td>
                          <span className={styles.monoRight}>{row.revenue}</span>
                        </td>
                        <td>
                          <span className={styles.monoRight}>{row.margin}</span>
                        </td>
                        <td>
                          <span className={styles.monoRight}>{row.employees}</span>
                        </td>
                        <td>
                          <span className={styles.monoRight}>{row.age}</span>
                        </td>
                        <td>
                          <span className={styles.mono}>{row.holder}</span>
                        </td>
                        <td>
                          <span className={styles.mono}>{row.filed}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.appFoot}>
                <span>14 accepted of 86 candidates before functional review</span>
                <span>·</span>
                <span>strategy TP-2026-0418</span>
                <span>·</span>
                <span>every financial field carries its source filing</span>
              </div>
            </div>
          </div>
        </div>

        <p className={styles.appCap}>
          {t(
            'Platform search. Counters are live figures; the result rows shown here are fictional entities used to illustrate a transfer pricing screen.',
          )}
        </p>
      </div>
    </Section>
  );
};

type FilterGroupRowProps = {
  group: FilterGroup;
  open: boolean;
  onToggle: (name: string | null) => void;
};

const FilterGroupRow = ({ group, open, onToggle }: FilterGroupRowProps) => (
  <div className={cn(styles.group, open && styles.groupOpen)}>
    <button
      type="button"
      className={styles.groupHead}
      onClick={() => onToggle(open ? null : group.name)}
      aria-expanded={open}
    >
      <span>{group.name}</span>
      <span className={styles.groupCount}>{group.count}</span>
      <span className={styles.groupChevron} />
    </button>

    <div className={styles.groupBody}>
      <div className={styles.groupBodyInner}>
        {group.fields.map((field) => (
          <div key={field.label} className={cn(styles.field, field.active && styles.fieldActive)}>
            <span>{field.label}</span>
            <em>{field.meta}</em>
          </div>
        ))}
      </div>
    </div>
  </div>
);
